import React, { useState } from 'react';

export default function CompetitorAnalysis({ user, onCreditsUpdate }) {
  const [channelsInput, setChannelsInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const rawInputs = channelsInput.split(',').map(c => c.trim()).filter(c => c);
      
      if (rawInputs.length < 2) {
        throw new Error("Please enter at least 2 channels or video URLs separated by a comma.");
      }
      if (rawInputs.length > 4) {
        throw new Error("You can analyze a maximum of 4 channels at once.");
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/analysis/competitor-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ channels: rawInputs })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || "Failed to fetch competitor analysis.");
      }

      if (data.success && data.data) {
        setResults(data.data);
        if (data.credits?.remaining !== undefined && onCreditsUpdate) {
          onCreditsUpdate(data.credits.remaining);
        }
      } else {
        throw new Error(data.message || "Unable to retrieve data for these channels.");
      }

    } catch (err) {
      console.error("Competitor Analysis Error:", err);
      setError(err.message || "Something went wrong while connecting to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="analysis-container" style={{ padding: '20px', maxWidth: '1300px', margin: '0 auto', color: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Header Section */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '30px', margin: '0 0 10px 0', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
          🏆 Competitor Intelligence Matrix
        </h2>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '15px' }}>
          Compare performance metrics across 2 to 4 competitor channels or videos. Use <strong>Channel Names</strong>, <strong>@handles</strong>, or <strong>Video Links</strong> (separated by commas).
        </p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleAnalyze} style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap', background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ flex: '1 1 300px' }}>
          <input 
            type="text" 
            value={channelsInput}
            onChange={(e) => setChannelsInput(e.target.value)}
            placeholder="e.g. Marques Brownlee, @MrBeast, https://youtube.com/watch?v=..."
            style={{ 
              width: '100%', 
              padding: '16px 20px', 
              borderRadius: '12px', 
              border: '1px solid rgba(255, 255, 255, 0.12)', 
              background: 'rgba(0, 0, 0, 0.3)', 
              color: '#ffffff',
              fontSize: '15px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            required 
          />
        </div>
        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            padding: '16px 30px', 
            borderRadius: '12px', 
            background: loading ? '#334155' : 'linear-gradient(90deg, #8b5cf6, #ec4899)', 
            color: 'white', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '15px',
            minWidth: '200px',
            transition: 'all 0.3s',
            boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
          }}
        >
          {loading ? '⏳ Analyzing...' : 'Analyze Competitors'}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.15)', borderLeft: '4px solid #ef4444', color: '#fca5a5', padding: '16px', borderRadius: '10px', marginBottom: '30px', fontSize: '15px' }}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Results Section */}
      {results && results.channels && (
        <div style={{ animation: 'fadeIn 0.5s ease-in' }}>
          
          {/* AI Insights Banner */}
          {results.insights && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', padding: '24px', borderRadius: '16px', marginBottom: '30px', borderLeft: '4px solid #8b5cf6', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
              <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#f8fafc', fontSize: '18px' }}>
                💡 AI Intelligence Insights
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                {results.insights.map((insight, idx) => (
                  <div key={idx} style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '16px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <span style={{ color: '#c4b5fd', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                      {insight.type.replace('_', ' ')}
                    </span>
                    <span style={{ color: '#cbd5e1', fontSize: '14px', lineHeight: '1.5' }}>
                      {insight.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Channel Comparison Cards */}
          <div style={{ display: 'flex', gap: '20px', overflowX: 'auto', paddingBottom: '15px' }}>
            {results.channels.map((channel, index) => (
              <div key={index} style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '24px', minWidth: '320px', flex: 1, border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                
                {/* Channel Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
                  <img src={channel.thumbnails?.high?.url || channel.thumbnails?.default?.url} alt={channel.title} style={{ width: '70px', height: '70px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', objectFit: 'cover' }} />
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                      {channel.title}
                    </h3>
                    <a href={`https://youtube.com/${channel.customUrl !== 'N/A' ? '@'+channel.customUrl : 'channel/'+channel.channelId}`} target="_blank" rel="noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '14px' }}>
                      {channel.customUrl !== 'N/A' ? '@'+channel.customUrl : 'Visit Channel'} ↗
                    </a>
                  </div>
                </div>
                
                {/* Metrics Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '25px' }}>
                  <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Subscribers</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#f8fafc' }}>{formatNumber(channel.subscriberCount)}</div>
                  </div>
                  <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Total Views</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#f8fafc' }}>{formatNumber(channel.viewCount)}</div>
                  </div>
                  <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Videos/Month</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#f8fafc' }}>{channel.analytics?.videosPerMonth || '0'}</div>
                  </div>
                  <div style={{ background: 'rgba(59, 130, 246, 0.15)', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                    <div style={{ fontSize: '11px', color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Engagement</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#60a5fa' }}>{channel.analytics?.engagementRate || 0}%</div>
                  </div>
                </div>

                {/* Recent Videos List */}
                <h4 style={{ fontSize: '13px', margin: '0 0 12px 0', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent Videos</h4>
                <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '4px' }}>
                  {channel.recentVideos && channel.recentVideos.length > 0 ? channel.recentVideos.slice(0, 4).map((vid, vIdx) => (
                    <div key={vIdx} style={{ marginBottom: '10px', background: 'rgba(0, 0, 0, 0.3)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <a href={`https://youtube.com/watch?v=${vid.videoId}`} target="_blank" rel="noreferrer" style={{ fontWeight: '600', color: '#f8fafc', textDecoration: 'none', display: 'block', marginBottom: '6px', fontSize: '13px', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {vid.title}
                      </a>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '12px', fontWeight: '500' }}>
                        <span>👁️ {formatNumber(vid.views)}</span>
                        <span>👍 {formatNumber(vid.likes)}</span>
                      </div>
                    </div>
                  )) : (
                    <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No recent videos found.</div>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
